import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useMe } from "./auth";
import { api } from "./api";

export type Language = "en" | "es" | "pt";

const SPANISH_COUNTRIES = new Set([
  "AR", "BO", "CL", "CO", "CR", "CU", "DO", "EC", "SV", "GQ",
  "GT", "HN", "MX", "NI", "PA", "PY", "PE", "ES", "UY", "VE"
]);

const PORTUGUESE_COUNTRIES = new Set([
  "BR", "PT", "AO", "MZ", "CV", "GW", "ST", "TL"
]);

const isMindfulMeHost = () => typeof window !== "undefined" && window.location.hostname.includes("mindfulme");

export function isLanguage(value: unknown): value is Language {
  return value === "en" || value === "es" || value === "pt";
}

let globalLanguage: Language = (() => {
  if (typeof window !== "undefined") {
    const local = localStorage.getItem("courses_language") as Language | null;
    if (local === "en" || local === "es" || local === "pt") return local;
  }
  return "en";
})();

function setDocumentLanguage(lang: Language) {
  if (typeof document !== "undefined") document.documentElement.lang = lang;
}

setDocumentLanguage(globalLanguage);

const listeners = new Set<(lang: Language) => void>();

export function getLanguage(): Language {
  return globalLanguage;
}

/** Sets the UI language locally (memory + localStorage) and notifies every useLanguage() consumer. */
export function applyLanguage(newLang: Language) {
  globalLanguage = newLang;
  setDocumentLanguage(newLang);
  try {
    localStorage.setItem("courses_language", newLang);
  } catch {}
  listeners.forEach((l) => l(newLang));
}

export function useLanguage() {
  const isMindfulMe = isMindfulMeHost();
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const [language, setLangState] = useState<Language>(globalLanguage);

  useEffect(() => {
    const listener = (lang: Language) => setLangState(lang);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const syncLanguage = applyLanguage;

  const setLanguage = async (newLang: Language) => {
    syncLanguage(newLang);

    if (me) {
      queryClient.setQueryData(["me"], (old: any) => old ? { ...old, language: newLang } : old);

      try {
        await api("/api/me/language", {
          method: "PUT",
          body: JSON.stringify({ language: newLang }),
        });
      } catch (err) {
        console.error("Failed to update language on backend:", err);
      }
    }
  };

  useEffect(() => {
    if (!me) return;

    if (me.language === "en" || me.language === "es" || me.language === "pt") {
      if (me.language !== language) {
        syncLanguage(me.language);
      }
      return;
    }

    const detectLanguage = async () => {
      let detected: Language = "en";

      const local = localStorage.getItem("courses_language");
      if (local === "en" || local === "es" || local === "pt") {
        detected = local;
      } else if (isMindfulMe) {
        // MindfulMe: the language comes from the funnel via the magic link (?lang=, see AuthCallback);
        // without it the report stays in English.
        detected = "en";
      } else {
        const funnel = me.funnelSource?.toLowerCase() || "";
        if (funnel.includes("pt") || funnel.includes("portuguese")) {
          detected = "pt";
        } else if (funnel.includes("es") || funnel.includes("spanish")) {
          detected = "es";
        } else {
          try {
            const mobileApiUrl = import.meta.env.VITE_MOBILE_API_URL || "http://localhost:3000";
            const res = await fetch(`${mobileApiUrl}/api/users/locale?email=${encodeURIComponent(me.email)}`);
            if (res.ok) {
              const data = await res.json();
              const country = (data.country || "").toUpperCase();
              const regFunnel = (data.funnel || "").toLowerCase();
              if (PORTUGUESE_COUNTRIES.has(country) || regFunnel.includes("pt") || regFunnel.includes("portuguese")) {
                detected = "pt";
              } else if (SPANISH_COUNTRIES.has(country) || regFunnel.includes("es") || regFunnel.includes("spanish")) {
                detected = "es";
              }
            }
          } catch (err) {
            console.warn("Failed to check registration locale:", err);
            if (navigator.language.startsWith("pt")) {
              detected = "pt";
            } else if (navigator.language.startsWith("es")) {
              detected = "es";
            }
          }
        }
      }

      syncLanguage(detected);
      queryClient.setQueryData(["me"], (old: any) => old ? { ...old, language: detected } : old);

      try {
        await api("/api/me/language", {
          method: "PUT",
          body: JSON.stringify({ language: detected }),
        });
      } catch (err) {
        console.error("Failed to sync detected language:", err);
      }
    };

    detectLanguage();
  }, [me]);

  return { language, setLanguage };
}
